<script setup>
import { i18n } from 'boot/i18n';
import { HrListItem, HrOverflowMenuBtn } from 'src/design-system';
import { computed } from 'vue';

import { todayDateStr } from '../../domain/services/effectiveDating';
import { presentCoverageRow } from '../presenters/coverageRowPresenter';

/**
 * Coverages past their effective end date.
 *
 * A static group, deliberately: no drag handle and no rank chip, because these have left the
 * coordination-of-benefits sequence. They are retained because a service date inside their period
 * can still be billed against them — but they take no part in today's ordering, and leaving them
 * draggable would invite someone to sort a dead plan above a live one.
 *
 * No row carries a status label. The section this list sits under already says these have ended, and
 * whether anyone had verified one first stops mattering once it has. There is no order chip either,
 * not even a placeholder, because these can never re-enter the order.
 *
 * Membership is derived from the end date, so a coverage arrives here on its own as reality moves.
 */
defineOptions({ name: 'EndedCoverageList' });

const props = defineProps({
  /** Ended coverages, most recently ended first. */
  coverages: {
    type: Array,
    default: () => [],
  },
  /** Funding-source refs, for resolving payer names. */
  fundingSources: {
    type: Array,
    default: () => [],
  },
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  edit: null,
  remove: null,
});

const asOf = todayDateStr();

const fundingSourceNameById = computed(() => {
  // Keyed by the PAYER — a coverage stores `fundingSourceId`, so that is what the lookup needs.
  return new Map(props.fundingSources
    .map((fundingSource) => [fundingSource.fundingSourceId, fundingSource.name]));
});

const rows = computed(() => {
  return props.coverages.map((coverage) => presentCoverageRow(coverage, {
    fundingSourceNameById: fundingSourceNameById.value,
    asOf,
  }));
});

const menuOptions = () => [
  { label: i18n.t('common.buttonEdit'), icon: 'edit', value: 'edit' },
  {
    group: 'destructive',
    options: [{
      label: i18n.t('common.buttonDelete'), icon: 'delete', value: 'remove', destructive: true,
    }],
  },
];

function onSelect(row, option) {
  if (option.value === 'edit') {
    emit('edit', row.coverage);
  } else if (option.value === 'remove') {
    emit('remove', row.coverage);
  }
} // onSelect()
</script>

<template>
  <hr-list-item
    v-for="row in rows"
    :key="row.id"
    segmented
    muted
    clickable
    :disable="disable"
    :label="row.label"
    :caption="row.caption"
    data-e2e="list-item-coverage-ended"
    @click="emit('edit', row.coverage)"
  >
    <template #trailing>
      <hr-overflow-menu-btn
        :options="menuOptions()"
        :disable="disable"
        @click.stop
        @select="onSelect(row, $event)"
      />
    </template>
  </hr-list-item>
</template>
